import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, ArrowRight, Loader2, Lock, CalendarCheck, LineChart, FileCheck2 } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { COUNTRIES, INTERESTS } from "@/constants";
import { analytics } from "@/services/analytics";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Reveal from "@/components/motion/Reveal";

// ─── Registration form ───────────────────────────────────────────────────────
const regInitial = { name: "", email: "", phone: "", country: "", interest: "", message: "" };

function RegistrationForm() {
  const [data, setData] = useState(regInitial);
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  const update = (k) => (e) => setData((d) => ({ ...d, [k]: e?.target ? e.target.value : e }));

  const validateEmail = (val) => {
    if (!val) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val)) return "Enter a valid email address.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const eErr = validateEmail(data.email);
    setEmailError(eErr);
    if (!data.name || !data.email || !data.country || !data.interest || eErr) {
      if (!eErr) toast.error("Please fill in name, email, country and interest.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/leads", data);
      analytics.consultationSubmit(data.interest);
      toast.success(res.data?.message || "Thanks — we'll be in touch.");
      setData(regInitial);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="lead-form"
      className="rounded-2xl bg-white border border-[hsl(var(--zt-beige-deep))] p-6 lg:p-8 elev-2 space-y-6"
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full name" required>
          <input
            data-testid="lead-name"
            className="input-editorial font-body"
            value={data.name}
            onChange={update("name")}
            placeholder="Anika Reddy"
            required
          />
        </Field>
        <Field label="Email" required>
          <input
            data-testid="lead-email"
            className={`input-editorial font-body ${emailError ? "!border-[hsl(var(--zt-negative))]" : ""}`}
            value={data.email}
            onChange={(e) => { update("email")(e); setEmailError(validateEmail(e.target.value)); }}
            onBlur={(e) => setEmailError(validateEmail(e.target.value))}
            type="email"
            placeholder="anika@example.com"
            required
          />
          {emailError && <p className="text-[hsl(var(--zt-negative))] text-xs mt-1.5 font-body">{emailError}</p>}
        </Field>
        <Field label="Phone (optional)">
          <input
            data-testid="lead-phone"
            className="input-editorial font-body"
            value={data.phone}
            onChange={update("phone")}
            placeholder="+1 555 0123"
          />
        </Field>
        <Field label="Country of residence" required>
          <Select value={data.country} onValueChange={update("country")}>
            <SelectTrigger
              data-testid="lead-country"
              className="h-[42px] rounded-[0.625rem] bg-white border border-[hsl(var(--zt-beige-deep))] px-3.5 font-body text-[0.9375rem] shadow-none focus:ring-2 focus:ring-[hsl(var(--zt-gold))]/15 focus:border-[hsl(var(--zt-gold))] data-[placeholder]:text-[hsl(var(--zt-muted))]/75"
            >
              <SelectValue placeholder="Select a country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`lead-country-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Primary interest" required className="sm:col-span-2">
          <Select value={data.interest} onValueChange={update("interest")}>
            <SelectTrigger
              data-testid="lead-interest"
              className="h-[42px] rounded-[0.625rem] bg-white border border-[hsl(var(--zt-beige-deep))] px-3.5 font-body text-[0.9375rem] shadow-none focus:ring-2 focus:ring-[hsl(var(--zt-gold))]/15 focus:border-[hsl(var(--zt-gold))] data-[placeholder]:text-[hsl(var(--zt-muted))]/75"
            >
              <SelectValue placeholder="What brings you to Zenstrust?" />
            </SelectTrigger>
            <SelectContent>
              {INTERESTS.map((c) => (
                <SelectItem key={c} value={c} data-testid={`lead-interest-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Message (optional)" className="sm:col-span-2">
          <textarea
            data-testid="lead-message"
            className="input-editorial font-body resize-none"
            rows={3}
            value={data.message}
            onChange={update("message")}
            placeholder="Tell us a bit about your goals…"
          />
        </Field>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pt-5 border-t border-[hsl(var(--zt-beige-deep))]">
        <p className="flex items-start gap-2 text-[12px] text-[hsl(var(--zt-muted))] max-w-xs leading-relaxed font-body">
          <Lock size={13} className="mt-0.5 flex-shrink-0 text-[hsl(var(--zt-sage))]" />
          Your details are encrypted and never shared with third parties.
        </p>
        <button
          type="submit"
          data-testid="lead-submit"
          disabled={submitting}
          className="group inline-flex items-center justify-center gap-2 bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/92 disabled:opacity-60 rounded-xl px-7 h-12 font-heading text-sm font-semibold tracking-tight elev-2 transition-all duration-200 hover:-translate-y-0.5"
        >
          {submitting ? <Loader2 className="animate-spin" size={16} /> : null}
          {submitting ? "Sending…" : "Request consultation"}
          {!submitting && <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />}
        </button>
      </div>
    </form>
  );
}

// ─── Survey form ─────────────────────────────────────────────────────────────
const surveyInitial = {
  name: "",
  country_of_residence: "",
  years_abroad: "",
  oci_card: "",
  oci_card_other: "",
  heard_gift_city: "",
  invested_gift_city: "",
  nri_app_frustrations: "",
  wants_educational_content: "",
};

function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className="flex flex-col gap-3 pt-2">
      {options.map((opt) => (
        <label key={opt} className="flex items-center gap-3 cursor-pointer group">
          <div className="relative flex items-center justify-center">
            <input
              type="radio"
              name={name}
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
              className="sr-only"
            />
            {/* Custom luxurious radio dot */}
            <div className={`w-[18px] h-[18px] rounded-full border transition-all duration-200 flex items-center justify-center ${
              value === opt 
                ? "border-[hsl(var(--zt-gold))] bg-[hsl(var(--zt-gold))]/10" 
                : "border-[hsl(var(--zt-teal))]/25 group-hover:border-[hsl(var(--zt-teal))]/50"
            }`}>
              {value === opt && (
                <div className="w-2 h-2 rounded-full bg-[hsl(var(--zt-gold))]" />
              )}
            </div>
          </div>
          <span className="text-sm text-[hsl(var(--zt-charcoal))]/80 group-hover:text-[hsl(var(--zt-charcoal))] transition-colors font-body">{opt}</span>
        </label>
      ))}
    </div>
  );
}

function SurveyForm() {
  const [data, setData] = useState(surveyInitial);
  const [submitting, setSubmitting] = useState(false);
  const [yearsAbroadError, setYearsAbroadError] = useState("");

  const update = (k) => (val) => setData((d) => ({ ...d, [k]: val }));
  const updateText = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const validateYearsAbroad = (val) => {
    if (!val.trim()) return "This field is required.";
    if (/^\d+$/.test(val.trim())) return "Please include a unit, e.g. '5 years' or '6 months'.";
    if (!/^(\d+\s*(year|years|month|months|yr|yrs|mo|mos))(\s+\d+\s*(month|months|mo|mos))?$/i.test(val.trim()))
      return "Enter a duration like '5 years', '6 months', or '2 years 3 months'.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const yErr = validateYearsAbroad(data.years_abroad);
    setYearsAbroadError(yErr);
    const required = ["name", "country_of_residence", "years_abroad", "oci_card", "invested_gift_city", "wants_educational_content"];
    if (required.some((k) => !data[k]) || yErr) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (data.oci_card === "Other" && !data.oci_card_other.trim()) {
      toast.error("Please specify your OCI card status.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...data };
      if (payload.oci_card !== "Other") delete payload.oci_card_other;
      const res = await api.post("/survey", payload);
      toast.success(res.data?.message || "Survey submitted. Thank you!");
      setData(surveyInitial);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl bg-white border border-[hsl(var(--zt-beige-deep))] p-6 lg:p-8 elev-2 space-y-6"
    >
      {/* Name */}
      <Field label="Name" required>
        <input
          className="input-editorial font-body"
          value={data.name}
          onChange={updateText("name")}
          placeholder="Your full name"
          required
        />
      </Field>

      {/* Country of residence */}
      <Field label="What is your country of residence?" required>
        <input
          className="input-editorial font-body"
          value={data.country_of_residence}
          onChange={updateText("country_of_residence")}
          placeholder="e.g. United States"
          required
        />
      </Field>

      {/* Years abroad */}
      <Field label="How long have you been living abroad?" required>
        <input
          className={`input-editorial font-body ${yearsAbroadError ? "!border-[hsl(var(--zt-negative))]" : ""}`}
          value={data.years_abroad}
          onChange={(e) => { updateText("years_abroad")(e); setYearsAbroadError(validateYearsAbroad(e.target.value)); }}
          onBlur={(e) => setYearsAbroadError(validateYearsAbroad(e.target.value))}
          placeholder="e.g. 5 years or 6 months"
          required
        />
        {yearsAbroadError && <p className="text-[hsl(var(--zt-negative))] text-xs mt-1.5 font-body">{yearsAbroadError}</p>}
      </Field>

      {/* OCI card */}
      <Field label="Do you have an OCI card?" required>
        <RadioGroup
          name="oci_card"
          options={["Yes", "No", "Other (Specify if you are on a visa etc)"]}
          value={data.oci_card === "Other" ? "Other (Specify if you are on a visa etc)" : data.oci_card}
          onChange={(val) => {
            const normalized = val.startsWith("Other") ? "Other" : val;
            setData((d) => ({ ...d, oci_card: normalized, oci_card_other: normalized !== "Other" ? "" : d.oci_card_other }));
          }}
        />
        {data.oci_card === "Other" && (
          <input
            className="input-editorial font-body mt-3"
            value={data.oci_card_other}
            onChange={updateText("oci_card_other")}
            placeholder="Please specify…"
          />
        )}
      </Field>

      {/* Heard about GIFT city */}
      <Field label="Have you heard about GIFT city">
        <RadioGroup
          name="heard_gift_city"
          options={["Yes", "No"]}
          value={data.heard_gift_city}
          onChange={update("heard_gift_city")}
        />
      </Field>

      {/* Invested in GIFT city */}
      <Field label="Have you invested or presently exploring to invest in GIFT city?" required>
        <RadioGroup
          name="invested_gift_city"
          options={["Yes", "No", "Maybe"]}
          value={data.invested_gift_city}
          onChange={update("invested_gift_city")}
        />
      </Field>

      {/* Frustrations */}
      <Field label="What are your biggest frustrations with the current NRI Investment App?">
        <textarea
          className="input-editorial font-body resize-none"
          rows={3}
          value={data.nri_app_frustrations}
          onChange={updateText("nri_app_frustrations")}
          placeholder="Share your thoughts…"
        />
      </Field>

      {/* Educational content */}
      <Field label="Would you like to see some educational content within the NRI Investment App">
        <RadioGroup
          name="wants_educational_content"
          options={["Yes", "No", "Maybe"]}
          value={data.wants_educational_content}
          onChange={update("wants_educational_content")}
        />
      </Field>

      <div className="flex justify-end pt-4 border-t border-[hsl(var(--zt-teal))]/10">
        <button
          type="submit"
          disabled={submitting}
          className="group inline-flex items-center justify-center gap-2 bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/92 disabled:opacity-60 rounded-xl px-7 h-12 font-heading text-sm font-semibold tracking-tight elev-2 transition-all duration-200 hover:-translate-y-0.5"
        >
          {submitting ? <Loader2 className="animate-spin" size={16} /> : <ArrowUpRight size={16} />}
          {submitting ? "Submitting…" : "Submit survey"}
        </button>
      </div>
    </form>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
const DELIVERABLES = [
  { icon: CalendarCheck, title: "30-minute strategy call", copy: "With a SEBI-registered cross-border advisor." },
  { icon: LineChart, title: "Custom asset sketch", copy: "A first-pass allocation for your goals and residency." },
  { icon: FileCheck2, title: "Compliance roadmap", copy: "DTAA, FEMA and repatriation mapped to your country." },
];

export default function LeadForm() {
  return (
    <section
      id="lead-form"
      data-testid="lead-form-section"
      className="py-20 lg:py-28 relative overflow-hidden border-t border-[hsl(var(--zt-beige-deep))]"
      style={{ background: "rgba(255,255,255,0.18)" }}
    >
      {/* Texture + ambient orbs */}
      <div aria-hidden className="absolute inset-0 dot-grid opacity-20 pointer-events-none" />
      <div
        aria-hidden
        className="absolute top-[-20%] right-[-10%] w-[520px] h-[520px] rounded-full opacity-[0.10] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-teal)) 0%, transparent 65%)" }}
      />
      <div
        aria-hidden
        className="absolute bottom-[-15%] left-[-8%] w-96 h-96 rounded-full opacity-[0.08] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-gold)) 0%, transparent 68%)" }}
      />

      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left copy */}
          <Reveal className="lg:col-span-5 space-y-7 lg:sticky lg:top-32">
            <div className="flex items-center gap-3">
              <span className="gold-rule" />
              <span className="eyebrow">Get started</span>
            </div>
            <h2 className="font-display text-[2rem] md:text-[2.5rem] leading-[1.1] text-[hsl(var(--zt-teal))]">
              Let's design your <span className="text-[hsl(var(--zt-gold))]">India strategy</span>.
            </h2>
            <p className="text-[15px] text-[hsl(var(--zt-charcoal))]/80 leading-[1.7] max-w-md font-body">
              Share your residency profile and objectives. We'll pair you with an advisor who knows both your
              host jurisdiction and the Indian market.
            </p>

            <ul className="space-y-3 pt-2 max-w-md">
              {DELIVERABLES.map(({ icon: Icon, title, copy }) => (
                <li key={title} className="flex items-start gap-3.5 rounded-xl border border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))] p-4">
                  <div className="w-9 h-9 rounded-lg bg-white border border-[hsl(var(--zt-beige-deep))] grid place-items-center flex-shrink-0">
                    <Icon size={15} className="text-[hsl(var(--zt-gold))]" />
                  </div>
                  <div>
                    <p className="font-heading text-[13.5px] font-semibold text-[hsl(var(--zt-teal))] leading-tight">{title}</p>
                    <p className="font-body text-[12.5px] text-[hsl(var(--zt-charcoal))]/75 leading-relaxed mt-1">{copy}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Right: registration form */}
          <Reveal delay={0.12} className="lg:col-span-7">
            <RegistrationForm />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block space-y-1.5 ${className}`}>
      <span className="font-heading text-[12.5px] font-semibold text-[hsl(var(--zt-teal))]">
        {label} {required && <span className="text-[hsl(var(--zt-gold))]" aria-hidden>*</span>}
      </span>
      {children}
    </label>
  );
}
